import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { activityCategoryLabels } from "@sp-crm/core";
import { ActivityChip, ActivityTargetChips } from "components/tasks/activity-chip";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { Link } from "react-router-dom";
import { contactPath } from "./agenda-actions";
import {
    AgendaCompletedTouchpoint,
    completedTypeName,
    formatCompletedAt,
    targetCategories,
} from "./marketing-plan-agenda";
import { scheduledVisitLabel } from "./marketing-plan-format";

interface AgendaRecentlyCompletedProps {
    items: AgendaCompletedTouchpoint[];
}

export const AgendaRecentlyCompleted: React.FC<AgendaRecentlyCompletedProps> = ({
    items,
}) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-lg bg-white ring-1 ring-gray-300">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <span className="h-5 w-1 flex-shrink-0 rounded-full bg-green-500" />
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
                <span className="text-sm font-semibold text-gray-800">
                    Completed this session
                </span>
                <span className="text-xs text-gray-500">{items.length}</span>
            </div>
            <ul className="divide-y divide-gray-100">
                <AnimatePresence initial={false}>
                    {items.map(item => (
                        <motion.li
                            key={`${item.contactId}|${item.touchpointId}`}
                            layout
                            initial={{
                                opacity: 0,
                                y: -8,
                                backgroundColor: "rgba(34, 197, 94, 0.18)",
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                backgroundColor: "rgba(34, 197, 94, 0)",
                            }}
                            transition={{
                                default: { type: "spring", stiffness: 500, damping: 30 },
                                backgroundColor: { duration: 1.6, ease: "easeOut" },
                            }}>
                            <CompletedRow item={item} />
                        </motion.li>
                    ))}
                </AnimatePresence>
            </ul>
        </div>
    );
};

const CompletedRow: React.FC<{ item: AgendaCompletedTouchpoint }> = ({ item }) => {
    const typeName = completedTypeName(item.targets);
    return (
        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
                <div className="flex-shrink-0 sm:w-24">
                    <CompletedChip item={item} />
                </div>
                <div className="min-w-0 flex-1">
                    <Link
                        to={contactPath(item.contactId)}
                        className="block truncate text-sm text-gray-900 hover:underline">
                        {item.contactName}
                    </Link>
                    {item.orgName ? (
                        <div className="truncate text-xs text-gray-500">
                            {item.orgName}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2 sm:w-64 sm:justify-end">
                {typeName ? (
                    <span className="truncate rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                        {typeName}
                    </span>
                ) : null}
                <span className="ml-auto flex-shrink-0 text-right text-[11px] tabular-nums text-gray-400 sm:ml-0">
                    {formatCompletedAt(item.completedAt)}
                </span>
            </div>
        </div>
    );
};

const CompletedChip: React.FC<{ item: AgendaCompletedTouchpoint }> = ({ item }) => {
    if (item.scheduledVisit) {
        return <span className="text-xs text-gray-500">{scheduledVisitLabel}</span>;
    }
    const categories = Array.from(targetCategories(item.targets));
    const only = categories.length === 1 ? categories[0] : null;
    if (only) {
        return <ActivityChip category={only} label={activityCategoryLabels[only]} />;
    }
    return (
        <div className="flex flex-wrap gap-1">
            <ActivityTargetChips targets={item.targets} />
        </div>
    );
};
